/* ══ MINTING — FROM _revPlan's OUTPUT, AND NEVER FROM A SECOND DIFF ════════════════════════
   ⚠ THIS IS A BUILD INSTRUCTION FROM THE SCOPE, NOT A CONVENIENCE. `_revWalk` is driven by
   MERGE_SPEC — "the SAME table drives the merge and this diff, so the two cannot disagree about
   what counts as a record". Computing our own diff here would be a SECOND definition of "a
   record changed", which is the exact class this repo keeps paying for (A52's two "blocked"
   definitions, chipLabel vs the board, the roster's two branches, no-store on the JSON but not
   the shell). So the ledger reads the plan the save is already making.

   ⚠ What it therefore cannot see, by design: `_revCanonObj` strips rev/writer/del, the child
   collections, REV_PROJECT_SKIP and REV_LOCAL_APPMETA — local and UI-only state. A collapsed
   fold is not work, and the ledger is a record of work. */

/* Short, enumerated fields only — ◆5. Anything not in here is CONTENT: we say THAT it changed
   and never what it now says, because a ledger is not a diff viewer and long text would swamp
   the page. ⚠ The old value is shown because IT EXISTS NOWHERE ELSE: the record keeps only the
   new one, so the save is the single moment the previous value is still known.
   v14.4 (c): the census was WIDENED on his instruction ("an intelligent depth of detail…
   without parroting content") — taken from the record shapes in this shell, not guessed:
   change requests name their person `requester` (the old list only had actions' `requestor`,
   so a CR requester change fell into the blanket content bucket) · stakeholders: role,
   organisation, power, interest, both engagement ends · comms: frequency, channel · KPIs:
   level · benefits: baseline, target, current · WBS: plannedStart, plannedFinish · actions:
   flagged, labels · improvement entries: raisedBy. */
/* ── ⚠⚠ v16.5 (b): THE NARROW ALLOW-LIST OF DOTTED LEAF PATHS ─────────────────────────────
   His report: "activity is not logging enough detail if a project status changes RAG. I am still
   not clear when we store more content about the activity in the ledger and when we just note a
   change in general."
   ⚠ THE ANSWER, AND IT IS NOT A JUDGEMENT ABOUT IMPORTANCE: a change earns old→new values only
   if its key sits on LOG_SHORT_FIELDS, matched against the record's TOP-LEVEL keys. Everything
   else that moved is named and never quoted (◆5). A project's RAG is appMeta.dashboard.rag —
   nested, AND an object — so it could ONLY ever render "overall status changed". There was no
   path through the mint that could have said green → amber.
   ⚠ ◆b-1 RULED NARROW (his call): an EXPLICIT list of leaf paths, not a general "any scalar leaf
   at any depth" rule. THE LEDGER IS APPEND-ONLY AND PERMANENT — a rule that logs too much cannot
   be taken back, and it would widen what every save by every writer writes for ever. Each further
   path is a deliberate one-line addition here; that is the point, not a shortcoming.
   ⚠ ◆b-2: the LEVEL travels, the RATIONALE does not. Free text stays a named content change —
   short fields stay short.
   ⚠ Paths are resolved against the SAME canonicalised record the short-field loop reads, so a
   path can never disagree with a field about what the record contained.
   ⚠ NO SCHEMA IMPACT: this changes only what a NEW ledger ENTRY contains. The ledger is its own
   file, the data part is untouched, SCHEMA_VERSION stays 10, and entries already on disk render
   exactly as before — the reader has always accepted both shapes and history is never rewritten. */
const LOG_SHORT_PATHS = ['rag.level', 'appMeta.dashboard.rag.level'];
const LOG_SHORT_FIELDS = ['status', 'priority', 'due', 'date', 'owner', 'requestor', 'requester',
                          'progress', 'rag', 'phase', 'type', 'category', 'closedDate',
                          'startTime', 'endTime', 'tz', 'location', 'impact', 'likelihood',
                          'severity', 'role', 'organisation', 'power', 'interest',
                          'engagementCurrent', 'engagementTarget', 'frequency', 'channel',
                          'level', 'flagged', 'labels', 'plannedStart', 'plannedFinish',
                          'baseline', 'target', 'current', 'raisedBy'];
/* v14.4 (c): a value formatted for a ledger line — a RECORD of the label at the time. Booleans
   say yes/no ("on agenda: true → false" is machine talk), arrays of primitives join, and
   anything long is cut: short fields must stay short. */
function _logFieldVal(v) {
  if (v === undefined || v === null || v === '') return '';
  if (v === true) return 'yes';
  if (v === false) return 'no';
  let s;
  if (Array.isArray(v)) s = v.filter(function (x) { return x != null && typeof x !== 'object'; }).join(', ');
  else s = String(v);
  /* ⚠ DAY-FIRST, THROUGH THE APP'S OWN FORMATTER — v13.1's A46 lesson, found again by looking
     at the rendered feed: a due-date row read "2026-09-01 → 2026-08-22" while every other date
     in the app reads "1 Sep 2026". A46 was the same mix in the agenda copy-out. Formatted at
     MINT time deliberately, because a ledger entry stores the label AS IT WAS AT THE TIME (his
     CEST principle) rather than re-deriving it on every read. */
  if (/^\d{4}-\d{2}-\d{2}$/.test(s) && typeof formatDateDisplay === 'function') {
    try { const d = formatDateDisplay(s); if (d) s = d; } catch (e) {}
  }
  return s.length > 60 ? s.slice(0, 60) + '…' : s;
}
/* v14.4 (c): bookkeeping keys are never a person's work — ids, revs, and the *Seq high-water
   marks. Minting a register record bumps its seq, which stamps the PROJECT; a row saying the
   project changed because a register minted an id is exactly the noise he reported. */
function _logNoiseKey(k) {
  /* v15.1: and any `_`-prefixed key — the convention for runtime-attached state the serialiser
     never writes (`_deletedIds` was the instance that reached his feed). REV_PROJECT_SKIP now
     strips that one from the canon at the root; this guard is defence in depth for the NEXT
     runtime key someone attaches, so it can never reach a ledger line even if the canon misses
     it. Nothing serializeDB writes starts with an underscore. */
  /* ⚠ v15.3 (b): and a MEETING's `diff`. It is DERIVED, not authored — step 1's
     `_captureAllFromDOM` recomputes it on every save from the action snapshot
     (`_curSession.diff = computeDiff(...)`), so editing an ACTION stamped the most recent
     meeting and the feed announced a meeting change he had not made. That is what he
     reported as the activity list showing minutes changed, and it is why his current session
     sat at rev 10, the most-stamped record in his file.
     ⚠ LEDGER ONLY — `REV_PROJECT_SKIP` is deliberately NOT touched and neither is the rev
     canon: `diff` is real data that must still merge between writers and still round-trip
     byte-exactly. Only the ROW is suppressed, and with `diff` as the sole content the row is
     dropped entirely — the v14.4 precedent, where a stamp that says nothing a human did
     writes no row and "the register's own row beside it is the story". Here the story is the
     ACTION's own row, which v15.3 (a) restores in the same release. */
  return k === 'rev' || k === 'writer' || k === 'del' || k === 'id' || k === 'num' ||
         k === 'uid' || k === 'diff' || /Seq$/.test(String(k)) || String(k).charAt(0) === '_';
}
/* v14.4 (c): one level into the project's appMeta, so its changes carry real names — the
   dashboard's notes, overall status, scope lists and gates are each their own thing, and
   "appMeta changed" would name none of them. Unknown keys fall back to their own name. */
function _logAppMetaNames(oldA, newA, out) {
  const oa = (oldA && typeof oldA === 'object') ? oldA : {};
  const na = (newA && typeof newA === 'object') ? newA : {};
  const keys = {};
  Object.keys(oa).forEach(function (k) { keys[k] = 1; });
  Object.keys(na).forEach(function (k) { keys[k] = 1; });
  Object.keys(keys).forEach(function (k) {
    if (_logNoiseKey(k)) return;
    if (_revStable(oa[k]) === _revStable(na[k])) return;
    if (k === 'dashboard') {
      const od = (oa.dashboard && typeof oa.dashboard === 'object') ? oa.dashboard : {};
      const nd = (na.dashboard && typeof na.dashboard === 'object') ? na.dashboard : {};
      const dk = {};
      Object.keys(od).forEach(function (x) { dk[x] = 1; });
      Object.keys(nd).forEach(function (x) { dk[x] = 1; });
      Object.keys(dk).forEach(function (x) {
        if (_logNoiseKey(x)) return;
        if (_revStable(od[x]) === _revStable(nd[x])) return;
        /* ⚠ v16.5 (b): the LEVEL now travels as a real old→new row via LOG_SHORT_PATHS
           ('appMeta.dashboard.rag.level'), so naming it here as well would print the same fact
           twice — "overall status green → amber · overall status changed". Name it ONLY when
           something else inside rag moved too, which in practice means the RATIONALE (◆b-2:
           free text stays a named change and never travels with values). */
        if (x === 'rag') {
          const or_ = (od.rag && typeof od.rag === 'object') ? od.rag : {};
          const nr_ = (nd.rag && typeof nd.rag === 'object') ? nd.rag : {};
          const rk = {};
          Object.keys(or_).forEach(function (y) { rk[y] = 1; });
          Object.keys(nr_).forEach(function (y) { rk[y] = 1; });
          const _other = Object.keys(rk).some(function (y) {
            return y !== 'level' && _revStable(or_[y]) !== _revStable(nr_[y]);
          });
          if (!_other) return;
        }
        out.push('dashboard.' + x);
      });
      return;
    }
    out.push('appMeta.' + k);
  });
}
/* ⚠ ONE PER REGISTER, AND THE LIST IS A CENSUS, NOT A GUESS. Every register names its own text
   field differently, and a missing name means a row that says "deleted" with nothing to say WHAT
   was deleted — which is the one thing the entry exists to preserve. Taken from the code:
   actions `desc` · risks and assumptions `statement` · stakeholders and benefits `name` ·
   workstreams and meetings `title` · change requests `description` · communications `what`.
   ⚠ Ordered most-specific first, because a meeting record also carries attendee `name`s. */
const LOG_TITLE_FIELDS = ['desc', 'statement', 'title', 'description', 'what', 'name', 'summary'];

function _logKindOf(path) {
  /* `projects/<uid>/actions/<id>` -> 'actions'; `projects/<uid>` -> 'project' */
  const segs = String(path || '').split('/');
  if (segs.length <= 2) return segs[0] === 'programmes' ? 'programme' : 'project';
  return segs[2];
}
const LOG_KIND_LABEL = {
  actions: 'action', risks: 'risk', stakeholders: 'person', assumptions: 'assumption',
  workstreams: 'workstream', sessions: 'meeting', 'appMeta.changeRequests': 'change request',
  'appMeta.communications': 'communication', 'appMeta.dashboard.kpis': 'KPI',
  'appMeta.improvements': 'improvement entry',   /* v14.4 (a) — or the feed prints the raw path */
  benefits: 'benefit', project: 'project', programme: 'programme'
};

/* The reference a row shows — ◆7: the register's own reference where one exists, the title
   otherwise. No invented identifiers. */
function _logRefFor(kind, rec, proj) {
  try {
    /* ⚠ v15.2: actions and risks derive ONLY when the record's project is the ACTIVE one —
       displayActionId reads the active project's edits and _registerId reads the active
       RISKS, so a multi-project save minted confident WRONG ordinals for the non-active
       projects' rows (ids collide across projects, so the by-key fallback found a match in
       the wrong project). ◆7 says no invented identifiers: the title alone is honest;
       a wrong R-004, stored forever, is worse than none. The improvements branch below
       derives against the record's OWN project and needed no gate. */
    if (kind === 'actions' && rec && rec.id != null && typeof displayActionId === 'function'
        && proj && proj.id === activeProjectId)
      return displayActionId(rec.id);
    if (kind === 'risks' && rec && typeof _registerId === 'function'
        && proj && proj.id === activeProjectId) return _registerId(rec);
    if (kind === 'appMeta.changeRequests' && rec && rec.num != null)
      return 'CR-' + String(rec.num).padStart(3, '0');
    /* v14.4 (a): derived against the entry's OWN project's list — the ordinal is per project,
       and at mint time this walk visits every project, not just the active one. */
    if (kind === 'appMeta.improvements' && rec && rec.id != null && proj && proj.appMeta &&
        Array.isArray(proj.appMeta.improvements))
      return 'CI-' + String(_mintDisplayOrd(proj.appMeta.improvements, rec)).padStart(3, '0');
  } catch (e) {}
  return '';
}
function _logTitleOf(rec) {
  if (!rec || typeof rec !== 'object') return '';
  for (let i = 0; i < LOG_TITLE_FIELDS.length; i++) {
    const v = rec[LOG_TITLE_FIELDS[i]];
    if (typeof v === 'string' && v.trim()) return v.trim().slice(0, 120);
  }
  return '';
}

/* Build ONE entry for this save: {at, changes:[…]} — ◆4, one row per SAVE with its changes
   nested. Returns null when nothing publishable changed, so a save that only moved UI state
   writes no entry at all. */
function _mintLogEntry(db, baseline, plan, whenISO) {
  if (!plan) return null;
  const projName = {}, projProg = {}, progName = {};
  (Array.isArray(db && db.programmes) ? db.programmes : []).forEach(function (p) {
    if (p && p.id != null) progName[p.id] = p.name || '';
  });
  (Array.isArray(db && db.projects) ? db.projects : []).forEach(function (p) {
    if (!p) return;
    projName[p.uid] = p.name || '';
    projProg[p.uid] = p.programmeId;
  });
  const recAt = function (path) {
    const segs = String(path).split('/');
    try {
      /* ⚠⚠ v16.5 (b): THE PROGRAMMES BRANCH WAS MISSING ENTIRELY, AND THAT IS A BIGGER DEFECT
         THAN THE ONE THIS WAVE SET OUT TO FIX. This resolver handled only 'projects', so every
         programme path fell through to { rec: null } — and the mint's own `if (old && rec)`
         guard then skipped the whole comparison. The row was still emitted (old was truthy, so
         the verb read 'changed'), but with NO fields and NO content.
         ⚠ SO EVERY PROGRAMME CHANGE EVER MADE HAS BEEN LOGGED WITH NO DETAIL AT ALL — not just
         RAG: renaming a programme, editing its rationale, changing a benefit's target. The feed
         said "changed programme X" and nothing more, for every one of them.
         ⚠ Found by MEASURING the mint's output rather than trusting the diagnosis: the wave was
         scoped for a nested-path problem, and the project half was exactly that — but the
         programme half never reached the code the scope was about. §v13.1's lesson again: look
         at what the thing actually produces.
         Benefits are the programme's only child collection (MERGE_SPEC.programme), so the shape
         below mirrors the projects branch one level shallower. */
      if (segs[0] === 'programmes') {
        const prog = (db.programmes || []).find(function (x) { return x && String(x.id) === segs[1]; });
        if (!prog) return { proj: null, rec: null };
        if (segs.length === 2) return { proj: null, rec: prog };
        const coll = prog[segs[2]];
        if (Array.isArray(coll)) {
          const rec = coll.find(function (r) {
            return r && (String(r.id) === segs[3] || String(r.num) === segs[3]);
          });
          return { proj: null, rec: rec || null };
        }
        return { proj: null, rec: null };
      }
      if (segs[0] === 'projects') {
        const proj = (db.projects || []).find(function (x) { return x && String(x.uid) === segs[1]; });
        if (!proj) return { proj: null, rec: null };
        if (segs.length === 2) return { proj: proj, rec: proj };
        let host = proj, parts = segs[2].split('.');
        for (let i = 0; i < parts.length - 1; i++) host = host && host[parts[i]];
        const coll = host && host[parts[parts.length - 1]];
        if (Array.isArray(coll)) {
          const rec = coll.find(function (r) {
            return r && (String(r.id) === segs[3] || String(r.num) === segs[3]);
          });
          return { proj: proj, rec: rec || null };
        }
        if (coll && typeof coll === 'object') return { proj: proj, rec: coll[segs[3]] || null };
        return { proj: proj, rec: null };
      }
    } catch (e) {}
    return { proj: null, rec: null };
  };
  const ctx = function (path, proj) {
    const segs = String(path).split('/');
    if (segs[0] === 'programmes')
      return { progId: segs[1], progName: progName[segs[1]] || '', projId: null, projName: '' };
    const uid = segs[1];
    const pid = projProg[uid];
    return { progId: (pid == null ? null : pid), progName: progName[pid] || '',
             projId: uid, projName: projName[uid] || '' };
  };
  const changes = [];

  plan.stamps.forEach(function (_stamp, path) {
    const kind = _logKindOf(path);
    const found = recAt(path);
    const rec = found.rec;
    const was = baseline ? baseline.get(path) : undefined;
    let old = null;
    if (was && typeof was.canon === 'string') { try { old = JSON.parse(was.canon); } catch (e) { old = null; } }
    const c = ctx(path, found.proj);
    const fields = [];
    const content = [];
    if (old && rec) {
      /* v14.4 (c): the NEW side goes through the SAME canonicaliser as the baseline did —
         _revCanonObj's "one rule, two callers" is now three. Without this, a project record's
         child collections and bookkeeping keys would read as phantom "content". */
      let now = rec;
      try {
        const segs2 = String(path).split('/');
        now = _revCanonObj(rec, segs2.length <= 2
          ? (segs2[0] === 'programmes' ? 'programme' : 'project') : null);
      } catch (e) {}
      /* ⚠ v16.5 (b): an OBJECT is never a short field. _logFieldVal ends in String(v), so an
         object rendered as the literal "[object Object]" — which is what a PROGRAMME's rag
         change wrote into the ledger, because `rag` IS a top-level key there and does sit on
         LOG_SHORT_FIELDS. Objects now fall through to the dotted paths (for the part that has
         values) and to content (for the rest). Arrays are NOT objects for this purpose —
         `labels` has always joined through _logFieldVal and still does. */
      const _objKeys = {};
      for (let i = 0; i < LOG_SHORT_FIELDS.length; i++) {
        const f = LOG_SHORT_FIELDS[i];
        const a = old[f], b = now[f];
        if (a === undefined && b === undefined) continue;
        const _isObj = (v) => (v && typeof v === 'object' && !Array.isArray(v));
        if (_isObj(a) || _isObj(b)) { _objKeys[f] = 1; continue; }
        if (_revStable(a) !== _revStable(b))
          fields.push({ f: f, from: _logFieldVal(a), to: _logFieldVal(b) });
      }
      /* v16.5 (b): the dotted leaf paths — the ONLY place a nested value earns old→new. */
      for (let i = 0; i < LOG_SHORT_PATHS.length; i++) {
        const _pth = LOG_SHORT_PATHS[i], _seg = _pth.split('.');
        const _dig = (o) => _seg.reduce((v, s) => (v && typeof v === 'object') ? v[s] : undefined, o);
        const a = _dig(old), b2 = _dig(now);
        if (a === undefined && b2 === undefined) continue;
        if (_revStable(a) !== _revStable(b2))
          fields.push({ f: _pth, from: _logFieldVal(a), to: _logFieldVal(b2) });
      }
      /* v14.4 (c): anything else that moved is CONTENT — NAMED now, still never quoted. The
         old blanket flag rendered the same three words ("description changed") whatever a
         colleague actually touched, which is what he reported: "not enough to just say
         description changed". Entries already on disk keep their boolean; the renderer reads
         both shapes, and nothing rewrites history. */
      const keys = {};
      Object.keys(old).forEach(function (k) { keys[k] = 1; });
      Object.keys(now).forEach(function (k) { keys[k] = 1; });
      /* ⚠ v16.5 (b): a key the short loop skipped as an OBJECT is named here instead — unless a
         dotted path already reported the only thing inside it that changed. Without that second
         half a programme RAG change would produce BOTH "RAG green → amber" AND "RAG changed"
         on the same line, which is the v13.1 A10 shape (two rows stating one fact). */
      const _pathCovered = function (k, leaf) {
        if (LOG_SHORT_PATHS.indexOf(k + '.' + leaf) === -1) return false;
        const oa = (old[k] && typeof old[k] === 'object') ? old[k] : {};
        const na = (now[k] && typeof now[k] === 'object') ? now[k] : {};
        const kk = {};
        Object.keys(oa).forEach(function (x) { kk[x] = 1; });
        Object.keys(na).forEach(function (x) { kk[x] = 1; });
        return !Object.keys(kk).some(function (x) {
          return x !== leaf && _revStable(oa[x]) !== _revStable(na[x]);
        });
      };
      Object.keys(keys).forEach(function (k) {
        if (_objKeys[k] && _pathCovered(k, 'level')) return;   /* v16.5 (b) */
        if (LOG_SHORT_FIELDS.indexOf(k) !== -1 && !_objKeys[k]) return;
        if (_logNoiseKey(k)) return;
        if (_revStable(old[k]) === _revStable(now[k])) return;
        if (k === 'appMeta') { _logAppMetaNames(old[k], now[k], content); return; }
        content.push(k);
      });
      /* A stamp whose only difference was bookkeeping (a *Seq mark riding appMeta) says
         nothing a human did — the register row beside it is the story. Skip the row, keep
         the stamp: the plan is untouched and the merge sees exactly what it always saw. */
      if (!fields.length && !content.length) return;
    }
    let verb = old ? 'changed' : 'added';
    /* "completed" reads better than "changed status to Completed", and it is the single
       transition a reader scans for. */
    if (old && rec && rec.status && old.status !== rec.status && isClosedStatus(rec.status))
      verb = (rec.status === 'Cancelled') ? 'cancelled' : 'completed';
    changes.push({ kind: kind, verb: verb, ref: _logRefFor(kind, rec, found.proj),
                   title: _logTitleOf(rec) || _logTitleOf(old),
                   progId: c.progId, prog: c.progName, projId: c.projId, proj: c.projName,
                   fields: fields, content: content.length ? content : false });
  });

  (plan.tombs || []).forEach(function (tomb) {
    const path = tomb.key;
    const kind = _logKindOf(path);
    const was = baseline ? baseline.get(path) : undefined;
    let old = null;
    if (was && typeof was.canon === 'string') { try { old = JSON.parse(was.canon); } catch (e) { old = null; } }
    const c = ctx(path, null);
    /* ⚠ The title is taken from the BASELINE, because the record is gone — the same reasoning
       as ◆5's old values: after this moment nothing anywhere remembers what it was called. */
    changes.push({ kind: kind, verb: 'deleted', ref: _logRefFor(kind, old, null),
                   title: _logTitleOf(old),
                   progId: c.progId, prog: c.progName, projId: c.projId, proj: c.projName,
                   fields: [], content: false });
  });

  if (!changes.length) return null;
  changes.sort(function (a, b) {
    return (a.proj || '').localeCompare(b.proj || '') || (a.kind || '').localeCompare(b.kind || '');
  });
  return { at: whenISO || _logNowISO(), changes: changes };
}

/* A local ISO stamp. ⚠ A RECORD, like savedAt: the feed sorts by it and NO merge logic ever
   reads it, so the counters-never-clocks rule is untouched. */
function _logNowISO() {
  try { return new Date().toISOString(); } catch (e) { return ''; }
}

/* ══ WRITING — v18: THE ENTRY TRAVELS WITH THE SAVE ════════════════════════════════════════════
   _mintLogEntry's output is sent in the same POST /api/save as the records it describes, and the
   server stores it in the same transaction, stamped with the server's clock and the session's
   writer. So the ledger can only ever claim things that really were saved, and a refused save
   logs nothing — the guarantee v14.3 got by writing the log only after the part write resolved. */

/* ══ READING — ONLY WHEN THE VIEW IS OPENED ════════════════════════════════════════════════
   Paged from GET /api/log, newest first, until the server says there is no more (capped, so a
   very long history cannot stall the pane). */
const LOG_FETCH_CAP = 20000;
let _logRows = null;           /* [{file, writer, entries, error}] — null = never loaded */
async function _loadAllLogs(force) {
  if (_logRows && !force) return _logRows;
  const entries = [];
  let before = null, error = null;
  try {
    do {
      const res = await _api('GET', '/api/log?limit=1000' + (before ? '&before=' + encodeURIComponent(before) : ''));
      (res.entries || []).forEach(e => entries.push(e));
      before = res.next || null;
    } while (before && entries.length < LOG_FETCH_CAP);
  } catch (e) { error = (e && e.message) || String(e); }
  /* One row per writer keeps the shape the Activity view has always rendered from. */
  const byWriter = new Map();
  entries.forEach(e => {
    const w = String(e.writer || '');
    if (!byWriter.has(w)) byWriter.set(w, { file: 'server', writer: w, entries: [], error: null });
    byWriter.get(w).entries.push({ at: e.at, changes: e.changes });
  });
  const rows = [...byWriter.values()];
  if (error) rows.push({ file: 'the server activity log', writer: '', entries: [], error: error });
  _logRows = rows;
  return rows;
}

/* The union across writers. ⚠ Append-only concatenation: no revs, no tombstones, no tie-break —
   nobody can write anybody else's log, so two logs simply concatenate and sort. That is
   trivially convergent, unlike the record merge. */
function _allLogEntries() {
  const out = [];
  (_logRows || []).forEach(function (row) {
    (row.entries || []).forEach(function (e) {
      if (e && typeof e === 'object') out.push({ w: row.writer, at: e.at || '', changes: Array.isArray(e.changes) ? e.changes : [] });
    });
  });
  out.sort(function (a, b) { return String(b.at).localeCompare(String(a.at)); });
  return out;
}
